import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Save, Upload, FileDown, RefreshCw, BarChart3, Loader2, Plus, Trash2, Zap } from "lucide-react";

/**
 * Templates admin page.
 *
 * Supports A/B experimentation and Multi-Armed Bandit routing (Thompson sampling).
 * Rows are grouped by (key, segment); each group can hold many variants (A, B, …).
 * When the runtime sends a message it calls services.pick_variant(key, segment):
 *   - If bandit mode is ON (Program Config → A/B auto-winner → ab_bandit_enabled)
 *     AND the key has a registered conversion signal → Thompson sampling.
 *   - Otherwise → weighted random (legacy A/B).
 * Segment options expose tenure buckets derived from the referrer doc:
 *     "*"             — global default (fallback)
 *     "tenure:1-2"    — newbies (<3 orders)
 *     "tenure:3-5"    — regulars
 *     "tenure:6+"     — loyal
 *
 * Operator workflow:
 *   1. Download sample CSV (includes a cross-segment row demo)
 *   2. Edit in Sheets/Excel (add rows with different `variant` + `segment` values)
 *   3. Upload CSV — all rows upsert by (key, variant, segment)
 *   4. Watch per-(variant, segment) lift under the Metrics section
 */
const SEGMENTS = [
  { value: "*", label: "Global (all)" },
  { value: "tenure:1-2", label: "Tenure 1–2 (newbies)" },
  { value: "tenure:3-5", label: "Tenure 3–5 (regulars)" },
  { value: "tenure:6+", label: "Tenure 6+ (loyal)" },
];

export default function Templates() {
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [program, setProgram] = useState(null);
  const [editing, setEditing] = useState({}); // { rowId: {body, active, weight, segment, ...} }
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [t, m, recent, promo, prog] = await Promise.all([
      api.get("/templates"),
      api.get("/templates/metrics?days=30"),
      api.get("/messages?limit=40"),
      api.get("/ab/promotions?limit=20"),
      api.get("/program/config"),
    ]);
    setItems(t.data);
    setMetrics(m.data);
    setMsgs(recent.data);
    setPromotions(promo.data);
    setProgram(prog.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };
  const rowId = (t) => `${t.key}::${t.segment || "*"}::${t.variant || "A"}`;

  // Group variants by (key, segment) for the UI
  const grouped = useMemo(() => {
    const g = {};
    for (const t of items) {
      const seg = t.segment || "*";
      const bucket = `${t.key}::${seg}`;
      (g[bucket] = g[bucket] || { key: t.key, segment: seg, variants: [] }).variants.push(t);
    }
    Object.values(g).forEach((b) => b.variants.sort((a, b) =>
      (a.variant || "A").localeCompare(b.variant || "A")));
    return g;
  }, [items]);

  // Lookup per-(variant, segment) metrics
  const metricsByRow = useMemo(() => {
    const m = {};
    (metrics?.keys || []).forEach((k) => {
      (k.variants || []).forEach((v) => {
        m[`${k.key}::${v.segment || "*"}::${v.variant}`] = v;
      });
    });
    return m;
  }, [metrics]);

  const banditOn = !!program?.ab_bandit_enabled;

  const saveRow = async (row) => {
    const id = rowId(row);
    const patch = editing[id] || {};
    const payload = { ...row, ...patch };
    try {
      await api.put(`/templates/${row.key}`, payload);
      setEditing((e) => ({ ...e, [id]: undefined }));
      flash(`Saved ${row.key} · ${payload.segment || "*"} · ${payload.variant}`);
      await load();
    } catch (err) { flash(formatApiError(err.response?.data?.detail) || "Save failed"); }
  };

  const addVariant = async (groupKey) => {
    const group = grouped[groupKey];
    if (!group) return;
    const taken = group.variants.map((v) => v.variant);
    const next = ["A", "B", "C", "D", "E"].find((v) => !taken.includes(v));
    if (!next) return flash("Already have variants A–E for this group");
    const source = group.variants[0];
    await api.put(`/templates/${group.key}`, {
      ...source, key: group.key, variant: next, segment: group.segment,
      weight: 1, active: true,
    });
    flash(`Added variant ${next} · ${group.segment}`);
    await load();
  };

  const addSegmentCopy = async (sourceGroup, newSegment) => {
    if (!sourceGroup || !newSegment) return;
    // Clone each variant body into the new segment so the operator starts from
    // a known baseline, then tweaks copy per-segment.
    for (const v of sourceGroup.variants) {
      await api.put(`/templates/${sourceGroup.key}`, {
        ...v, key: sourceGroup.key, variant: v.variant, segment: newSegment,
        weight: 1, active: true,
      });
    }
    flash(`Forked ${sourceGroup.key} into segment ${newSegment}`);
    await load();
  };

  const deleteVariant = async (row) => {
    const seg = row.segment || "*";
    if (!window.confirm(`Delete ${row.key} · ${seg} · ${row.variant}?`)) return;
    try {
      await api.delete(`/templates/${row.key}/${row.variant}?segment=${encodeURIComponent(seg)}`);
      flash(`Deleted ${row.key} · ${seg} · ${row.variant}`);
      await load();
    } catch (err) { flash(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/templates/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      flash(`Inserted ${data.inserted} · Updated ${data.updated} · Invalid ${data.invalid}`);
      await load();
    } catch (err) { flash(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resetSeed = async () => {
    if (!window.confirm("Wipe all templates and restore baked-in defaults?")) return;
    try {
      await api.post("/templates/reset");
      flash("Templates reset to defaults");
      await load();
    } catch (err) { flash(formatApiError(err.response?.data?.detail) || "Reset failed"); }
  };

  const groupKeys = Object.keys(grouped).sort();

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
            Message Templates
            {banditOn && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#fff0f5] px-2 py-0.5 text-[11px] font-bold text-[#c01974]" data-testid="bandit-badge">
                <Zap className="h-3 w-3" /> Bandit mode
              </span>
            )}
          </h1>
          <p className="text-sm text-[#5c665a]">
            Group = <span className="font-bold">(key, segment)</span>. Each group can hold multiple <span className="font-bold">variants</span> (A, B, C…). The runtime routes by segment first, then picks a variant per-send — weighted random by default, or <span className="font-bold">Thompson sampling</span> when Bandit mode is on.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a className="admin-btn-ghost" href={`${API}/templates/sample.csv`} download data-testid="templates-sample">
            <FileDown className="h-4 w-4" /> Sample CSV
          </a>
          <label className="admin-btn cursor-pointer">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Upload CSV"}
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={upload} data-testid="templates-upload" />
          </label>
          <button className="admin-btn-ghost" onClick={resetSeed} data-testid="templates-reset">
            <RefreshCw className="h-4 w-4" /> Reset to defaults
          </button>
        </div>
      </div>

      {toast && <div className="rounded-lg bg-[#eaf8de] px-4 py-2 text-sm font-semibold text-[#3f7c21]" data-testid="templates-toast">{toast}</div>}

      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#66cc33]" /></div>
      ) : (
        <div className="space-y-5" data-testid="templates-grouped">
          {groupKeys.map((gk) => {
            const group = grouped[gk];
            const segmentLabel = SEGMENTS.find((s) => s.value === group.segment)?.label || group.segment;
            // Other segments you could fork to (only the ones missing for this key)
            const forkable = SEGMENTS
              .filter((s) => !groupKeys.includes(`${group.key}::${s.value}`))
              .map((s) => s.value);
            return (
              <div key={gk} className="admin-card" data-testid={`group-${group.key}-${group.segment}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">{group.variants[0]?.experiment || group.key}</div>
                    <h3 className="flex items-center gap-2 font-mono text-base font-bold text-[#1a1c19]">
                      {group.key}
                      <span className="rounded-full bg-[#eaf8de] px-2 py-0.5 text-[10px] font-semibold text-[#3f7c21]" data-testid={`segment-tag-${group.key}-${group.segment}`}>
                        {segmentLabel}
                      </span>
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {forkable.length > 0 && (
                      <select
                        className="admin-input py-1 text-xs"
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) {
                            addSegmentCopy(group, e.target.value);
                            e.target.value = "";
                          }
                        }}
                        data-testid={`fork-segment-${group.key}-${group.segment}`}
                      >
                        <option value="">+ Fork to segment…</option>
                        {forkable.map((s) => (
                          <option key={s} value={s}>{SEGMENTS.find((x) => x.value === s)?.label || s}</option>
                        ))}
                      </select>
                    )}
                    <button className="admin-btn-ghost" onClick={() => addVariant(gk)} data-testid={`add-variant-${group.key}-${group.segment}`}>
                      <Plus className="h-3.5 w-3.5" /> Add variant
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 lg:grid-cols-2">
                  {group.variants.map((t) => {
                    const id = rowId(t);
                    const patch = editing[id] || {};
                    const val = { ...t, ...patch };
                    const vs = metricsByRow[id] || {};
                    return (
                      <div key={id} className="rounded-xl border border-[#e2e8e2] bg-white p-4"
                           data-testid={`template-${t.key}-${group.segment}-${t.variant}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#eaf8de] text-sm font-extrabold text-[#3f7c21]">{t.variant}</span>
                            <label className="flex items-center gap-1 text-[11px] font-semibold text-[#5c665a]">
                              <input type="checkbox" checked={!!val.active}
                                     onChange={(e) => setEditing({ ...editing, [id]: { ...val, active: e.target.checked } })}
                                     data-testid={`template-active-${t.key}-${group.segment}-${t.variant}`} />
                              Active
                            </label>
                            <input type="number" min="1" max="100" value={val.weight || 1}
                                   onChange={(e) => setEditing({ ...editing, [id]: { ...val, weight: Number(e.target.value) } })}
                                   className="admin-input w-14 text-xs"
                                   disabled={banditOn}
                                   title={banditOn ? "Weights are ignored when bandit mode is on" : ""}
                                   data-testid={`template-weight-${t.key}-${group.segment}-${t.variant}`} />
                            <span className="text-[10px] text-[#5c665a]">{banditOn ? "θ~Beta" : "weight"}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button className="admin-btn" onClick={() => saveRow(val)}
                                    data-testid={`save-template-${t.key}-${group.segment}-${t.variant}`}>
                              <Save className="h-3.5 w-3.5" /> Save
                            </button>
                            <button className="admin-btn-ghost text-[#c01974]"
                                    onClick={() => deleteVariant(t)}
                                    data-testid={`delete-template-${t.key}-${group.segment}-${t.variant}`}
                                    title="Delete this variant">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        <textarea
                          rows={4}
                          value={val.body}
                          onChange={(e) => setEditing({ ...editing, [id]: { ...val, body: e.target.value } })}
                          className="admin-input mt-3 font-mono text-sm"
                          data-testid={`template-body-${t.key}-${group.segment}-${t.variant}`}
                        />
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px]">
                          <span className="text-[#5c665a]">Vars: <code>{`{{name}} {{link}} {{reward}} {{min_sub}} {{discount}} {{max}} {{review}}`}</code></span>
                          {vs.sends > 0 && (
                            <span className="rounded-full bg-[#fafcfa] px-2 py-0.5 font-bold text-[#1a1c19]">
                              {vs.sends} sends · {vs.unique_recipients} recipients
                              {typeof vs.conversion_rate_pct === "number" && <> · conv <span className="text-[#66cc33]">{vs.conversion_rate_pct}%</span></>}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lift summary */}
      {metrics && metrics.keys.some((k) => (k.variants || []).length > 1) && (
        <div className="admin-card">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-[#5c665a] flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[#ff3399]" /> A/B lift (30d, per segment)
          </h3>
          <div className="grid gap-3 md:grid-cols-2">
            {metrics.keys.filter((k) => (k.variants || []).length > 1).map((k) => (
              <div key={k.key} className="rounded-lg border border-[#e2e8e2] p-3">
                <div className="font-mono text-xs font-bold text-[#1a1c19]">{k.key}</div>
                <table className="mt-2 w-full text-xs">
                  <thead className="text-left text-[#5c665a]">
                    <tr><th>Variant</th><th>Segment</th><th>Sends</th><th>Reach</th><th>Conv %</th></tr>
                  </thead>
                  <tbody className="font-mono">
                    {k.variants.map((v) => (
                      <tr key={`${v.variant}-${v.segment || "*"}`}>
                        <td className="py-0.5">{v.variant}</td>
                        <td className="text-[#5c665a]">{v.segment || "*"}</td>
                        <td>{v.sends}</td>
                        <td>{v.unique_recipients}</td>
                        <td>{typeof v.conversion_rate_pct === "number" ? `${v.conversion_rate_pct}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      {promotions.length > 0 && (
        <div className="admin-card" data-testid="promotions-history">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-[#5c665a] flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[#66cc33]" /> Auto-promotion history
          </h3>
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-[#5c665a]">
              <tr><th className="py-2">When</th><th>Key</th><th>Segment</th><th>Method</th><th>Winner</th><th>Runner-up</th><th>Winner rate</th><th>Lift (pp)</th><th>Evidence</th></tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8e2]">
              {promotions.map((p) => (
                <tr key={p.id} data-testid={`promotion-${p.id}`}>
                  <td className="py-2 text-xs text-[#5c665a]">{p.decided_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="font-mono text-xs">{p.key}</td>
                  <td className="text-xs text-[#5c665a]">{p.segment || "*"}</td>
                  <td className="text-xs">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.method === "sequential_bayesian" ? "bg-[#fff0f5] text-[#c01974]" : "bg-[#eaf8de] text-[#3f7c21]"}`}>
                      {p.method === "sequential_bayesian" ? "Bayesian" : "Z-test"}
                    </span>
                  </td>
                  <td><span className="rounded-full bg-[#eaf8de] px-2 py-0.5 text-[11px] font-bold text-[#3f7c21]">{p.winner}</span></td>
                  <td className="text-xs">{p.runner_up}</td>
                  <td className="font-mono text-xs">{p.winner_rate}%</td>
                  <td className="font-bold text-[#66cc33]">+{p.lift_pct_points}</td>
                  <td className="font-mono text-[11px] text-[#5c665a]">
                    {p.method === "sequential_bayesian"
                      ? `P(winner)=${Math.round((p.prob_winner || 0) * 1000) / 10}%`
                      : `z=${p.z}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-card overflow-x-auto p-0">
        <div className="border-b border-[#e2e8e2] px-4 py-3">
          <h3 className="font-semibold">Recent outbound messages</h3>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
            <tr><th className="px-4 py-3">Channel</th><th className="px-4 py-3">Template</th><th className="px-4 py-3">Variant</th><th className="px-4 py-3">Segment</th><th className="px-4 py-3">Recipient</th><th className="px-4 py-3">Body</th><th className="px-4 py-3">When</th></tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8e2]">
            {msgs.slice(0, 30).map((m) => (
              <tr key={m.id}>
                <td className="px-4 py-2"><span className="badge-green">{m.channel}</span></td>
                <td className="px-4 py-2 font-mono text-xs">{m.template}</td>
                <td className="px-4 py-2 text-xs">{m.variant || "—"}</td>
                <td className="px-4 py-2 text-xs text-[#5c665a]">{m.segment || "—"}</td>
                <td className="px-4 py-2 text-xs">{m.recipient}</td>
                <td className="px-4 py-2 max-w-xs truncate text-xs text-[#5c665a]" title={m.body}>{m.body}</td>
                <td className="px-4 py-2 text-xs text-[#5c665a]">{m.created_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
